import { IANA_TIMEZONE_SET, IANA_TIMEZONES_2026C } from '@src/components/sco/Advisors/data/ianaTimezones2026c'
import { browserTimezone } from '@src/components/sco/Advisors/utils/sessionFormat'

export type TimezoneOption = { label: string; value: string }

export type TimezoneGroup = { name: string; items: TimezoneOption[] }

const GROUP_ORDER = [
  'Africa',
  'America',
  'Antarctica',
  'Arctic',
  'Asia',
  'Atlantic',
  'Australia',
  'Europe',
  'Indian',
  'Pacific',
  'Etc',
  'Other',
] as const

const GROUP_ORDER_SET = new Set<string>(GROUP_ORDER)

export const timezoneLabel = (id: string): string => id.replaceAll('_', ' ')

/** Match combobox labels against a typed query (`New York` or `New_York`). */
export const matchesTimezoneQuery = (itemText: string, query: string): boolean => {
  const normalizedQuery = query.trim().toLocaleLowerCase()
  if (!normalizedQuery) {
    return true
  }
  const normalizedItem = itemText.toLocaleLowerCase()
  return (
    normalizedItem.includes(normalizedQuery) ||
    normalizedItem.replaceAll(' ', '_').includes(normalizedQuery)
  )
}

export const toTimezoneOption = (value: string): TimezoneOption => ({
  label: timezoneLabel(value),
  value,
})

/** Saved profile timezone, or the viewer's local IANA zone when none is stored. */
export const defaultSessionTimezone = (saved?: string | null): string => {
  const trimmed = saved?.trim()
  return trimmed || browserTimezone()
}

export const timezoneGroupName = (id: string): string => {
  const slash = id.indexOf('/')
  if (slash <= 0) {
    return 'Other'
  }
  const prefix = id.slice(0, slash)
  return GROUP_ORDER_SET.has(prefix) ? prefix : 'Other'
}

export const timezoneOptions = (extra?: string | null): TimezoneOption[] => {
  const items = IANA_TIMEZONES_2026C.map(toTimezoneOption)
  const trimmed = extra?.trim()
  if (trimmed && !IANA_TIMEZONE_SET.has(trimmed)) {
    items.unshift(toTimezoneOption(trimmed))
  }
  return items
}

export const groupTimezoneOptions = (items: TimezoneOption[]): TimezoneGroup[] => {
  const buckets = new Map<string, TimezoneOption[]>()
  for (const item of items) {
    const name = timezoneGroupName(item.value)
    const list = buckets.get(name)
    if (list) {
      list.push(item)
    } else {
      buckets.set(name, [item])
    }
  }

  const grouped: TimezoneGroup[] = []
  for (const name of GROUP_ORDER) {
    const list = buckets.get(name)
    if (list?.length) {
      grouped.push({ name, items: list })
      buckets.delete(name)
    }
  }
  for (const [name, list] of buckets) {
    grouped.push({ name, items: list })
  }
  return grouped
}
